import './config/amplify'
import './i18n/config'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter } from 'react-router-dom'
import { AuthProvider } from './hooks/useAuth'
import { AppThemeProvider } from './ThemeContext'
import { ErrorBoundary } from './components/ErrorBoundary'
import { PreferencesProvider } from './preferences/PreferencesContext'
import { PlaylistAudioProvider } from './audio/PlaylistAudioContext'
import { KidModeProvider } from './contexts/KidModeContext'
import { initAnalytics } from './lib/analytics'
import App from './App'
import './index.css'

// Init Google Analytics 4. No-op when VITE_GA_MEASUREMENT_ID is unset
// (dev/test) or when the user advertises Do Not Track — gtag never
// gets injected, no traffic fires.
initAnalytics({ measurementId: import.meta.env.VITE_GA_MEASUREMENT_ID ?? '' })

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

// Provider order matters:
//   ErrorBoundary → BrowserRouter → AuthProvider → PreferencesProvider →
//     AppThemeProvider → PlaylistAudioProvider → <App />
//
// AppThemeProvider sits INSIDE PreferencesProvider because it reads
// `prefs.kidMode` to switch between the standard and kid-friendly MUI
// themes. Keeping it outside would force every kid-mode-affecting style
// change to go through a context other than prefs, which would split the
// source of truth.
//
// PlaylistAudioProvider hoists the playlist audio engine above the Routes so
// playback survives navigation. It depends on PreferencesContext (reciter +
// edition) and BrowserRouter must be its ancestor too — the mini-player it
// surfaces uses `useLocation` to hide itself on the playlist detail page.
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <PreferencesProvider>
              <KidModeProvider>
                <AppThemeProvider>
                  <PlaylistAudioProvider>
                    <App />
                  </PlaylistAudioProvider>
                </AppThemeProvider>
              </KidModeProvider>
            </PreferencesProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  </React.StrictMode>
)
